import { useMutation } from '@apollo/client/react';
import classNames from 'classnames';
import React, { useRef, useState } from 'react';
import { useAddEmail } from '../../../hooks/apolloHooks/useAddEmail';
import { useDeleteEmail } from '../../../hooks/apolloHooks/useDeleteEmail';
import { useGetCurrentUserProfile } from '../../../hooks/apolloHooks/useGetCurrentUserProfile';
import { useGetCustomer } from '../../../hooks/apolloHooks/useGetCustomer';
import { useGetDeliveryTypes } from '../../../hooks/apolloHooks/useGetDeliveryTypes';
import { useGetOwnerCustomerRoles } from '../../../hooks/apolloHooks/useGetOwnerCustomerRoles';
import { InvoiceMailingDeliveryType, useUpdateInvoiceDelivery } from '../../../hooks/apolloHooks/useUpdateInvoiceDelivery';
import { GET_BASIC_CUSTOMER_DATA_QUERY, GET_CUSTOMER_RIGHTS_DATA_QUERY } from '../../../integrations/allkort/graphql/gql/CustomerQueries';
import { ADD_CUSTOMER_TASK_MUTATION } from '../../../integrations/allkort/graphql/gql/CustomerTasksMutations';
import { BasicCustomerDataResponse, ContactDataResponse, CustomerRightsDataResponse } from '../../../integrations/allkort/graphql/interface/Customer';
import { AddCustomerTaskInput, AddCustomerTaskResponse } from '../../../integrations/allkort/graphql/interface/CustomerTaskMutation';
import { EmailTypeId } from '../../../integrations/allkort/graphql/interface/Emails';
import { validateEmail } from '../../../utils/contactValidationSchemas';
import { validateEinvoiceExchange, validateEinvoiceGLN, validateEinvoiceId } from '../../../utils/einvoiceValidationSchemas';
import { EMAIL_TYPES } from '../../../utils/filterEmailsByType';
import { CheckmarkCircleIcon, CloseIcon, InfoIcon, InfoIconOutlined, PenIcon, SwitchArrowsIcon } from '../../svgs/ExpressonSVGs';
import Button from '../Button';
import Link from '../Link';
import RightsWrapper from '../RightsWrapper';
import Tooltip from '../Tooltip';
import VerifyEmail from '../VerifyEmail';
import AutogiroSettings from './AutogiroSettings';
import CardEInvoiceDelivery from './CardEInvoiceDelivery';
import CardEmailDelivery from './CardEmailDelivery';
import CardLetterDelivery from './CardLetterDelivery';

type ProfileCreditCardProps = {
    wrapperClasses?: string;
    isCompany: boolean | undefined;
    customerContactData: ContactDataResponse;
    invoiceEmailType?: EmailTypeId;
    refetchContactData?: () => void;
}

type DraftEmail =
    | { kind: 'existing'; id: number; address: string; verified: boolean }
    | { kind: 'new'; address: string }

type PendingDialogType = InvoiceMailingDeliveryType | null

type EInvoiceType = 'Peppol' | 'VAN'

const ProfileCreditCard: React.FC<ProfileCreditCardProps> = ({ wrapperClasses = '', isCompany, customerContactData, invoiceEmailType = 'INVOICE_STATEMENT', refetchContactData }) => {
    const [isManageMode, setIsManageMode] = useState(false);
    const [pendingDeliveryType, setPendingDeliveryType] = useState<PendingDialogType>(null);
    const [draftEmails, setDraftEmails] = useState<DraftEmail[]>([]);
    const [newEmailInput, setNewEmailInput] = useState('');
    const [newEmailError, setNewEmailError] = useState<string | undefined>(undefined);
    const [isNewEmailValid, setIsNewEmailValid] = useState(false);
    const [isAddingEmail, setIsAddingEmail] = useState(false);
    const [pendingVerification, setPendingVerification] = useState<{ email: string; emailId: number } | null>(null);
    const [isEmailVerifying, setIsEmailVerifying] = useState(false);
    const [selectedEinvoiceType, setSelectedEinvoiceType] = useState<EInvoiceType>('Peppol');
    const [einvoiceId, setEinvoiceId] = useState('');
    const [einvoiceIdError, setEinvoiceIdError] = useState<string | undefined>(undefined);
    const [einvoiceExchange, setEinvoiceExchange] = useState('');
    const [einvoiceExchangeError, setEinvoiceExchangeError] = useState<string | undefined>(undefined);
    const [einvoiceGLN, setEinvoiceGLN] = useState('');
    const [einvoiceGLNError, setEinvoiceGLNError] = useState<string | undefined>(undefined);
    const [einvoiceReference, setEinvoiceReference] = useState('');
    const [einvoiceReferenceError, setEinvoiceReferenceError] = useState<string | undefined>(undefined);
    const [einvoiceSubmitted, setEinvoiceSubmitted] = useState(false);
    const [addCustomerTask, { loading: einvoiceTaskLoading }] =
        useMutation<AddCustomerTaskResponse, AddCustomerTaskInput>(ADD_CUSTOMER_TASK_MUTATION);

    const { invoiceDelivery, statementDelivery, refetch: refetchDeliveryTypes } = useGetDeliveryTypes();
    const { updateInvoiceDelivery, loading: updateInvoiceDeliveryLoading } = useUpdateInvoiceDelivery();
    const { addEmail } = useAddEmail();
    const { deleteEmail } = useDeleteEmail();

    const { data: customerBasicData } = useGetCustomer<BasicCustomerDataResponse>(
        GET_BASIC_CUSTOMER_DATA_QUERY,
        undefined,
        { fetchPolicy: 'cache-first' }
    );
    const customerId = customerBasicData?.currentCustomer?.id ?? null;

    const { data: customerRightsData } = useGetCustomer<CustomerRightsDataResponse>(
        GET_CUSTOMER_RIGHTS_DATA_QUERY,
        {},
        { fetchPolicy: 'network-only' }
    );
    const isFrozen = customerRightsData?.currentCustomer?.frozen?.is ?? false;
    const hasBlockingState = (customerRightsData?.currentCustomer?.state ?? null) !== null;
    // TODO: we need to check for specific rights related to invoice delivery management here, not just any write rights
    const { userProfile } = useGetCurrentUserProfile();
    const { roles: availableRoles, isComplete: rolesComplete } = useGetOwnerCustomerRoles();
    const availableWriteClaimNames = rolesComplete
        ? availableRoles.filter(r => r.claimName.includes('mutation')).map(r => r.claimName)
        : [];
    const isSuperUser = (userProfile?.roles ?? []).some(r => r.claimName === 'Superuser');
    const hasWriteRights = isSuperUser || (availableWriteClaimNames.length > 0 &&
        availableWriteClaimNames.every(name => (userProfile?.roles ?? []).some(r => r.claimName === name)));

    const canInteract = !isFrozen && !hasBlockingState && hasWriteRights;

    const handleEinvoiceIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value && !/^[\d:]*$/.test(value)) return;
        setEinvoiceId(value);
        if (value.length === 0) {
            setEinvoiceIdError(undefined);
            return;
        }
        const result = validateEinvoiceId(value);
        setEinvoiceIdError(result.valid ? undefined : result.error);
    };

    const handleEinvoiceExchangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setEinvoiceExchange(value);
        if (value.length === 0) {
            setEinvoiceExchangeError(undefined);
            return;
        }
        const result = validateEinvoiceExchange(value);
        setEinvoiceExchangeError(result.valid ? undefined : result.error);
    };

    const handleEinvoiceGLNChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value && !/^\d*$/.test(value)) return;
        setEinvoiceGLN(value);
        if (value.length === 0) setEinvoiceGLNError(undefined);
    };

    const handleEinvoiceGLNBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value.length === 0) return;
        const result = validateEinvoiceGLN(value);
        setEinvoiceGLNError(result.valid ? undefined : result.error);
    };

    const handleEinvoiceReferenceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value && !/^[a-zA-ZåäöÅÄÖ\d .:\-]*$/.test(value)) return;
        setEinvoiceReference(value);
        if (value.length === 0) setEinvoiceReferenceError(undefined);
    };

    const handleEinvoiceReferenceBlur = (_e: React.FocusEvent<HTMLInputElement>) => {
        // Optional field — no minimum length requirement, chars already filtered in onChange
    };

    const isEinvoiceFormValid = (): boolean => {
        if (!validateEinvoiceId(einvoiceId).valid) return false;
        if (!validateEinvoiceExchange(einvoiceExchange).valid) return false;
        if (einvoiceGLN.length > 0 && !validateEinvoiceGLN(einvoiceGLN).valid) return false;
        if (einvoiceReferenceError) return false;
        return true;
    };

    const handleSubmitEInvoice = async () => {
        if (!customerId || !isEinvoiceFormValid()) return;
        const currentDate = new Date();
        const dueDate = new Date(currentDate.getTime() + 60 * 60 * 1000); // 1 hour in the future
        const dateStr = currentDate.toLocaleDateString('sv-SE');
        let description = `Ansökan om E-faktura\r\n`;
        description += `Datum: ${dateStr}\r\n`;
        description += `Typ: ${selectedEinvoiceType === 'Peppol' ? 'Peppol-nätverket' : 'VAN-leverantör'}\r\n`;
        description += `ID: ${einvoiceId}\r\n`;
        description += `E-fakturaväxel: ${einvoiceExchange}\r\n`;
        if (einvoiceGLN.length > 0) description += `GLN: ${einvoiceGLN}\r\n`;
        if (einvoiceReference.length > 0) description += `Referens: ${einvoiceReference}\r\n`;
        try {
            await addCustomerTask({
                variables: {
                    customerId: Number(customerId),
                    description,
                    dueDate: dueDate.toISOString(),
                },
            });
            setEinvoiceSubmitted(true);
        } catch (err) {
            console.error('Failed to submit e-invoice request:', err);
        }
    };

    const handleUpdateInvoiceDelivery = async (customerId: number, deliveryType: InvoiceMailingDeliveryType) => {
        try {
            await updateInvoiceDelivery({ customerId, deliveryType });
        } catch (err) {
            console.error('Failed to update invoice delivery:', err);
        }
    };

    const dialogRef = useRef<HTMLDialogElement>(null);

    const invoiceEmails = (customerContactData?.currentCustomer?.emails || [])
        .filter(e => e.type.id === EMAIL_TYPES[invoiceEmailType as keyof typeof EMAIL_TYPES]);

    const openDialog = (deliveryType: Exclude<PendingDialogType, null>) => {
        if (!canInteract) return;

        const initial: DraftEmail[] = deliveryType === InvoiceMailingDeliveryType.EMAIL_DISPATCH
            ? invoiceEmails.map(e => ({ kind: 'existing', id: e.id, address: e.address, verified: e.verified }))
            : [];
        setDraftEmails(initial);
        setNewEmailInput('');
        setNewEmailError(undefined);
        setIsNewEmailValid(false);
        setIsManageMode(false);
        setPendingVerification(null);
        setPendingDeliveryType(deliveryType);
        dialogRef.current?.showModal();
    };

    const openManageDialog = () => {
        if (!canInteract) return;
        const initial = invoiceEmails.map(e => ({ kind: 'existing' as const, id: e.id, address: e.address, verified: e.verified }));
        setDraftEmails(initial);
        setNewEmailInput('');
        setNewEmailError(undefined);
        setIsNewEmailValid(false);
        setIsManageMode(true);
        setPendingVerification(null);
        setPendingDeliveryType(InvoiceMailingDeliveryType.EMAIL_DISPATCH);
        dialogRef.current?.showModal();
    };

    const handleNewEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setNewEmailInput(value);
        if (value.length === 0) {
            setNewEmailError(undefined);
            setIsNewEmailValid(false);
            return;
        }
        const result = validateEmail(value);
        setIsNewEmailValid(result.valid);
        setNewEmailError(result.valid ? undefined : result.error);
    };

    const handleAddDraftEmail = async () => {
        const trimmed = newEmailInput.trim();
        const result = validateEmail(trimmed);
        if (!result.valid || draftEmails.some(e => e.address === trimmed) || !customerId) return;
        setIsAddingEmail(true);
        try {
            const added = await addEmail({ address: trimmed, customerId, emailType: invoiceEmailType });
            const emailId = added?.[0]?.id;
            if (emailId) {
                setPendingVerification({ email: trimmed, emailId });
                setNewEmailInput('');
                setNewEmailError(undefined);
                setIsNewEmailValid(false);
            }
        } catch (err) {
            console.error('[ProfileCreditCard] addEmail failed:', err);
        } finally {
            setIsAddingEmail(false);
        }
    };

    const removeDraftEmail = (index: number) => {
        const email = draftEmails[index];
        setDraftEmails(prev => prev.filter((_, i) => i !== index));
        if (email.kind === 'existing') {
            deleteEmail({ emailId: email.id }).catch(err =>
                console.error('[ProfileCreditCard] deleteEmail failed:', err)
            );
        }
    };

    const handleConfirmDeliveryChange = async () => {
        if (!pendingDeliveryType || !customerId) return;
        // Email adds/removes are already instant; only switch delivery type here
        if (!isManageMode) {
            await handleUpdateInvoiceDelivery(customerId, pendingDeliveryType);
        }
        dialogRef.current?.close();
        setPendingDeliveryType(null);
        setIsManageMode(false);
        refetchDeliveryTypes();
        refetchContactData?.();
    };

    const handleCancel = async () => {
        // If the user added an email but didn't finish verification, remove it from the backend
        if (pendingVerification) {
            await deleteEmail({ emailId: pendingVerification.emailId });
            setPendingVerification(null);
        }

        // Trigger refetch on close modal so you have the latest data in view after api calls.
        const hasNewAdditions = draftEmails.some(e => e.kind === 'existing' && !originalEmailIds.has(e.id));
        if (isManageMode && (hasEmailChanges || hasNewAdditions)) {
            refetchContactData?.();
        }

        const shouldFallbackToPaper = pendingDeliveryType === InvoiceMailingDeliveryType.EMAIL_DISPATCH && draftEmails.length === 0;
        if (shouldFallbackToPaper && customerId) {
            await handleUpdateInvoiceDelivery(customerId, InvoiceMailingDeliveryType.PAPER_DISPATCH);
            refetchDeliveryTypes();
        }
        dialogRef.current?.close();
        setPendingDeliveryType(null);
        setIsManageMode(false);
        setEinvoiceSubmitted(false);
        setEinvoiceId('');
        setEinvoiceIdError(undefined);
        setEinvoiceExchange('');
        setEinvoiceExchangeError(undefined);
        setEinvoiceGLN('');
        setEinvoiceGLNError(undefined);
        setEinvoiceReference('');
        setEinvoiceReferenceError(undefined);
    };

    const originalEmailIds = new Set(invoiceEmails.map(e => e.id));
    const remainingEmailIds = new Set(
        draftEmails.filter((e): e is Extract<DraftEmail, { kind: 'existing' }> => e.kind === 'existing').map(e => e.id)
    );
    // Only pending deletions matter — adds are already persisted via addEmail+verify
    const hasEmailChanges = [...originalEmailIds].some(id => !remainingEmailIds.has(id));
    const hasVerifiedEmail = draftEmails.some(e => e.kind === 'existing' && e.verified);

    const cardLetter = isCompany ? "F" : "T";
    const cardTypeName = isCompany ? "Företagskort" : "Tankkort";

    const getInvoiceDeliveryTypeName = () => {
        if (pendingDeliveryType === InvoiceMailingDeliveryType.EMAIL_DISPATCH) return 'e-post';
        if (pendingDeliveryType === InvoiceMailingDeliveryType.PAPER_DISPATCH) return 'brev';
        if (pendingDeliveryType === InvoiceMailingDeliveryType.E_INVOICE) return 'e-faktura';
    }

    return (
        <div className={classNames(
            'profile-settings-credit-card',
            'profilesettingscreditcard_component',
            'white-pale-section',
            'shadow-one',
            'with-header',
            wrapperClasses
        )}>
            <div className="header">
                <h2 className="body-text">
                    <span
                        className={classNames(
                            "creditcard-icon",
                            "card-icon",
                            { "company": isCompany }
                        )}
                    >
                        {cardLetter}
                    </span>
                    {cardTypeName}
                    <Tooltip text="Hantera hur du vill få din faktura levererad samt om du vill ha autogiro." position="top-end">
                        <InfoIconOutlined width="26" height="26" />
                    </Tooltip>
                </h2>
            </div>

            {invoiceDelivery !== null ? (
                <div className="content">
                    <h5 className="body-text">
                        Faktura
                        {invoiceDelivery?.deliveryType.id === 4 && (
                            <RightsWrapper
                                disableButtonText='Hantera e-postadresser'
                                disableButtonIcon={<PenIcon width={18} height={18} />}
                                disableButtonClasses={classNames('body-text')}
                            >
                                <Link
                                    href={'#'}
                                    className="edit-action"
                                    onClick={() => openManageDialog()}
                                >
                                    <PenIcon width={18} height={18} />
                                    Hantera e-postadresser
                                </Link>
                            </RightsWrapper>
                        )}
                    </h5>

                    <p>
                        Välj hur du vill få din faktura levererad till dig.
                    </p>

                    <div className="delivery-methods">
                        <CardEmailDelivery
                            cardType="K"
                            isActiveMethod={invoiceDelivery?.deliveryType.id === 4}
                            customerContactData={customerContactData}
                            onSelect={() => openDialog(InvoiceMailingDeliveryType.EMAIL_DISPATCH)}
                        />

                        <CardLetterDelivery
                            cardType="K"
                            isActiveMethod={invoiceDelivery?.deliveryType.id === 1}
                            customerContactData={customerContactData}
                            onSelect={() => openDialog(InvoiceMailingDeliveryType.PAPER_DISPATCH)}
                        />

                        <CardEInvoiceDelivery
                            isActiveMethod={invoiceDelivery?.deliveryType.id === 2 || invoiceDelivery?.deliveryType.id === 3}
                            customerContactData={customerContactData}
                            isCompany={isCompany || false}
                            onSelect={() => openDialog(InvoiceMailingDeliveryType.E_INVOICE)}
                        />
                    </div>
                    {/* Autogiro component to handle autogiro settings */}
                    <AutogiroSettings customerId={customerId} />
                </div>
            ) : (
                <div className="content">
                    <h5 className="body-text">Faktura</h5>
                    <p>
                        Något gick fel, kontakta <Link className="link" href="/kontakt">kundservice</Link> för att ändra leveranssätt på dina fakturor.
                    </p>
                </div>
            )}



            {/* Dialog to manage delivery methods */}
            <dialog
                ref={dialogRef}
                className={classNames(
                    "edit-delivery-method-dialog",
                    { 'einvoice-delivery': (isCompany && pendingDeliveryType === InvoiceMailingDeliveryType.E_INVOICE) }
                )}
                onCancel={handleCancel}
            >
                <div className="card-delivery__dialog-content">
                    <h2 className="card-delivery__title body-text">
                        <span className="svg-icon-wrapper orange round">
                            <SwitchArrowsIcon width={28} height={28} />
                        </span>
                        {isManageMode ? 'Hantera e-postadresser' : `Byt leveranssätt till ${getInvoiceDeliveryTypeName()}`}
                    </h2>

                    {/* ── E-POST ── */}
                    {(isManageMode || pendingDeliveryType === InvoiceMailingDeliveryType.EMAIL_DISPATCH) && (
                        <>
                            <p>
                                {isManageMode
                                    ? invoiceDelivery?.deliveryType.id === 4
                                        ? 'Hantera vilka e-postadresser som får dina fakturor och kontobesked.'
                                        : 'Hantera vilka e-postadresser som får dina fakturor.'
                                    : invoiceDelivery?.deliveryType.id === 4
                                        ? 'Dina fakturor och kontobesked kommer att skickas via e-post. Välj vilka e-postadresser du vill använda nedan.'
                                        : 'Dina fakturor kommer att skickas via e-post. Välj vilka e-postadresser du vill använda nedan.'}
                            </p>
                            <div className="select-emails">
                                <div className="add-email">
                                    <div>
                                        <input
                                            type="email"
                                            placeholder="Lägg till e-postadress"
                                            name="newInvoiceEmail"
                                            value={newEmailInput}
                                            onChange={handleNewEmailChange}
                                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddDraftEmail(); } }}
                                        />
                                        <Button
                                            type="button"
                                            className="add-email-button body-text"
                                            disabled={!isNewEmailValid || !!pendingVerification || isAddingEmail}
                                            onClick={handleAddDraftEmail}
                                        >
                                            Lägg till
                                        </Button>
                                    </div>
                                    {newEmailError && newEmailInput.length > 0 && (
                                        <span className="input-error">{newEmailError}</span>
                                    )}
                                </div>
                                {draftEmails.length > 0 && (
                                    <div className="receipt-emails__list">
                                        {draftEmails.map((email, index) => (
                                            <div
                                                key={email.kind === 'existing' ? email.id : `new-${email.address}`}
                                                className={classNames('receipt-email', {
                                                    'receipt-email--new': email.kind === 'new',
                                                    'receipt-email--unverified': email.kind === 'existing' && !email.verified,
                                                })}
                                            >
                                                <span
                                                    className="receipt-email__address"
                                                    onClick={() => removeDraftEmail(index)}
                                                >
                                                    {email.address}
                                                    <CloseIcon width={24} height={24} />
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {draftEmails.some(email => email.kind === 'existing' && !email.verified) && (
                                    <p className="input-error">Vissa e-postadresser är inte verifierade. De är markerade i rött, vänligen ta bort dem och lägg till igen för att gå igenom verifieringsprocessen.</p>
                                )}

                                {/* Loader to indicate email is being added */}
                                {isAddingEmail && (
                                    <span className="loader-zoom"></span>
                                )}
                                {/* Pending verification */}
                                {pendingVerification && (
                                    <VerifyEmail
                                        email={pendingVerification.email}
                                        emailId={pendingVerification.emailId}
                                        onVerifySuccess={() => setIsEmailVerifying(true)}
                                        onVerified={(verifiedEmail) => {
                                            const emailId = pendingVerification!.emailId;
                                            setDraftEmails(prev => [...prev, { kind: 'existing', id: emailId, address: verifiedEmail, verified: true }]);
                                            setPendingVerification(null);
                                            setIsEmailVerifying(false);
                                        }}
                                    />
                                )}
                            </div>
                        </>
                    )}

                    {/* ── BREV ── */}
                    {!isManageMode && pendingDeliveryType === InvoiceMailingDeliveryType.PAPER_DISPATCH && (
                        <>
                            <p>Dina fakturor kommer att skickas via brev till din folkbokföringsadress.</p>
                            <p>Faktura via brev kostar 25kr/månad</p>
                        </>

                    )}

                    {/* ── E-FAKTURA ── */}
                    {!isManageMode && pendingDeliveryType === InvoiceMailingDeliveryType.E_INVOICE && (
                        <div className="card-delivery__einvoice-form">
                            {!isCompany ? (
                                <>
                                    <p>
                                        Logga in på din internetbank och följ instruktionerna för att ansluta e-faktura.
                                        När du söker på Qstar ska du välja <strong>QSTAR FÖRSÄLJNING AB</strong>.
                                    </p>
                                    <p>
                                        Glöm inte ange ditt kundnummer vid ansökan, det hittar du nedan.
                                    </p>
                                    <span>
                                        <strong>
                                            Kundnummer:
                                        </strong>
                                        {customerContactData?.currentCustomer?.customerNumber || 'N/A'}
                                        <Tooltip position='top' text={'Ditt kundnummer behövs för att ansluta e-faktura. Använd det när du söker efter Qstar på din internetbank.'} >
                                            <InfoIconOutlined width={22} height={22} />
                                        </Tooltip>
                                    </span>
                                    <p>När du ansökt tar det några arbetsdagar innan leveransättet uppdateras på din profil.</p>
                                </>
                            ) : (
                                <>
                                    <div className="intro">
                                        <div className="intro-content-text">
                                            <p>
                                                Dina fakturor kommer att skickas till din bank som e-faktura. För att ansluta e-faktura välj vilket sätt nedan.
                                            </p>
                                            <span>
                                                <strong>
                                                    Kundnummer:
                                                </strong>
                                                {customerContactData?.currentCustomer?.customerNumber || 'N/A'}
                                                <Tooltip position='top' text={'Ditt kundnummer behövs för att ansluta e-faktura med enskild firma. Använd det när du söker efter Qstar på din internetbank.'} >
                                                    <InfoIconOutlined width={20} height={20} />
                                                </Tooltip>
                                            </span>
                                        </div>

                                        <div className="info">
                                            <p className="italic small-text">
                                                <InfoIcon width={22} height={22} />

                                                Om du har enskild firma så behöver du ansöka e-faktura via din bank.
                                                Glöm inte ange ditt kundnummer vid ansökan.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="einvoice-content">
                                        <form id="request-einvoice-form">
                                            <div className="radio-buttons-wrapper">
                                                <span>
                                                    Välj E-fakturatjänst<span className="red-text">*</span>
                                                    <Tooltip position='top' text={'Välj den tjänst du önskar ansluta för att ta emot e-faktura.'}>
                                                        <InfoIconOutlined width={20} height={20} />
                                                    </Tooltip>
                                                </span>

                                                <div className="radio-buttons">
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="cardType"
                                                            value={'Peppol'}
                                                            checked={selectedEinvoiceType === 'Peppol'}
                                                            onChange={(e) => setSelectedEinvoiceType(e.target.value as EInvoiceType)}
                                                        />
                                                        Peppol-nätverket
                                                    </label>

                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="cardType"
                                                            value="VAN"
                                                            checked={selectedEinvoiceType === 'VAN'}
                                                            onChange={(e) => setSelectedEinvoiceType(e.target.value as EInvoiceType)}
                                                        />
                                                        VAN-leverantör
                                                    </label>
                                                </div>
                                            </div>

                                            <div className="input-fields-wrapper">
                                                <div className="id-input input-wrapper">
                                                    <span>
                                                        ID<span className="red-text">*</span>
                                                        <Tooltip position='top' text="ID för Peppol-nätverket är vanligtvis ditt organisationsnummer utan bindestreck. För VAN-leverantör, kontakta din VAN-leverantör för att få rätt ID.">
                                                            <InfoIconOutlined width={20} height={20} />
                                                        </Tooltip>
                                                    </span>

                                                    <input
                                                        type="text"
                                                        name="einvoiceId"
                                                        placeholder="1234..."
                                                        value={einvoiceId}
                                                        onChange={handleEinvoiceIdChange}
                                                        maxLength={20}
                                                    />
                                                    {einvoiceIdError && einvoiceId.length > 0 && (
                                                        <span className="input-error">{einvoiceIdError}</span>
                                                    )}
                                                </div>

                                                <div className="einvoice-exchange-input input-wrapper">
                                                    <span>
                                                        E-fakturaväxel<span className="red-text">*</span>
                                                        <Tooltip position='top' text="fyll på med text här">
                                                            <InfoIconOutlined width={20} height={20} />
                                                        </Tooltip>
                                                    </span>

                                                    <input
                                                        type="text"
                                                        name="einvoiceExchange"
                                                        placeholder="..."
                                                        value={einvoiceExchange}
                                                        onChange={handleEinvoiceExchangeChange}
                                                        maxLength={30}
                                                    />
                                                    {einvoiceExchangeError && einvoiceExchange.length > 0 && (
                                                        <span className="input-error">{einvoiceExchangeError}</span>
                                                    )}
                                                </div>

                                                <div className="einvoice-GLN-input input-wrapper">
                                                    <span>
                                                        GLN<span>(valfri)</span>
                                                        <Tooltip position='top' text="GLN (Global Location Number) är ett 13-siffrigt unikt ID som identifierar en plats, juridisk enhet eller funktion i en leveranskedja.">
                                                            <InfoIconOutlined width={20} height={20} />
                                                        </Tooltip>
                                                    </span>

                                                    <input
                                                        type="text"
                                                        name="einvoiceGLN"
                                                        placeholder="123456..."
                                                        value={einvoiceGLN}
                                                        onChange={handleEinvoiceGLNChange}
                                                        onBlur={handleEinvoiceGLNBlur}
                                                        maxLength={13}
                                                    />
                                                    {einvoiceGLNError && einvoiceGLN.length > 0 && (
                                                        <span className="input-error">{einvoiceGLNError}</span>
                                                    )}
                                                </div>

                                                <div className="einvoice-reference-input input-wrapper">
                                                    <span>
                                                        Referens<span>(valfri)</span>
                                                        <Tooltip position='top' text="Referens är en valfri text som kan användas för att identifiera eller kategorisera fakturor i din bank. Det kan vara användbart för att snabbt se vad fakturan avser när du tittar på den i din internetbank.">
                                                            <InfoIconOutlined width={20} height={20} />
                                                        </Tooltip>
                                                    </span>

                                                    <input
                                                        type="text"
                                                        name="einvoiceReference"
                                                        placeholder=""
                                                        value={einvoiceReference}
                                                        onChange={handleEinvoiceReferenceChange}
                                                        onBlur={handleEinvoiceReferenceBlur}
                                                        maxLength={25}
                                                    />
                                                    {einvoiceReferenceError && einvoiceReference.length > 0 && (
                                                        <span className="input-error">{einvoiceReferenceError}</span>
                                                    )}
                                                </div>
                                            </div>

                                        </form>
                                    </div>
                                </>

                            )}
                        </div>
                    )}

                    <div className="card-delivery__actions">
                        {einvoiceSubmitted ? (
                            <>
                                <p className="einvoice-success-text">
                                    <CheckmarkCircleIcon width={22} height={22} />
                                    Din ansökan om e-faktura har skickats till kundservice och kommer att hanteras inom kort.
                                </p>
                                <Button
                                    type="button"
                                    onClick={handleCancel}
                                    className="button-cancel body-text"
                                >
                                    Stäng
                                </Button>
                            </>
                        ) : (
                            <>
                                {pendingDeliveryType === InvoiceMailingDeliveryType.E_INVOICE && isCompany ? (
                                    <Button
                                        className="body-text"
                                        type="button"
                                        onClick={handleSubmitEInvoice}
                                        disabled={einvoiceTaskLoading || !isEinvoiceFormValid()}
                                    >
                                        {einvoiceTaskLoading ? 'Skickar...' : 'Skicka ansökan'}
                                    </Button>
                                ) : pendingDeliveryType !== InvoiceMailingDeliveryType.E_INVOICE && !isManageMode ? (
                                    <Button
                                        className="body-text"
                                        type="button"
                                        onClick={handleConfirmDeliveryChange}
                                        disabled={updateInvoiceDeliveryLoading || !!pendingVerification || (isManageMode ? !hasEmailChanges || !hasVerifiedEmail : (pendingDeliveryType === InvoiceMailingDeliveryType.EMAIL_DISPATCH && (draftEmails.length === 0 || !hasVerifiedEmail)))}
                                    >
                                        {updateInvoiceDeliveryLoading ? 'Sparar...' : isManageMode ? 'Uppdatera e-postadresser' : 'Byt leveranssätt'}
                                    </Button>
                                ) : null}
                                {pendingDeliveryType === InvoiceMailingDeliveryType.EMAIL_DISPATCH && invoiceDelivery?.deliveryType.id !== 1 && draftEmails.length === 0 && (
                                    <div className="input-info">
                                        <InfoIconOutlined width="26" height="26" />
                                        <span>
                                            Väljer du ingen e-postadress ändras leveransättet till brev som kostar 25kr/månaden.
                                        </span>
                                    </div>
                                )}
                                <Button
                                    type="button"
                                    onClick={handleCancel}
                                    className="button-cancel body-text"
                                    disabled={updateInvoiceDeliveryLoading || einvoiceTaskLoading || isEmailVerifying}
                                >
                                    {isManageMode ? 'Tillbaka' : ((pendingDeliveryType !== InvoiceMailingDeliveryType.E_INVOICE) || (isCompany && pendingDeliveryType === InvoiceMailingDeliveryType.E_INVOICE) ? 'Avbryt' : 'Stäng')}
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </dialog>
        </div>
    )
}

export default ProfileCreditCard;